import axios, { AxiosResponse } from 'axios'
import { useState } from 'react'
import { TFunction } from 'react-i18next'
import { encryptAES } from '../../util/cipher'
import useLoadingStore from '../../stores/useLoadingStore'
import ConfigAPI from '../../constant/ConfigAPI'
import { v4 as uuidv4 } from 'uuid'
import useRelogin from '../useRelogin'

export type Response = { result?: AxiosResponse<any>; error?: any }

export interface Form {
    username: string
    isyl: string
    rememberPassword?: boolean
}

export type LoginResponse = {
    loginProfile: {
        email: string
        fullname: string
        groupName: string
    }
    permissions: string[]
}

export type ValidateUserResponse = {
    valid: boolean
    email: string
    verifyToken: string
    errCode: string
}

export type AutLoginResponse = {
    success: boolean
    accessToken: string
    refreshToken: string
}

export const validate = async (values: Form, t: TFunction<'translation'>) => {
    const errors: Partial<Form> = {}
    if (!values.username) {
        errors.username = `${t('LABEL.TEXT_REQUIRED')}`
    }
    if (!values.isyl) {
        errors.isyl = `${t('LABEL.TEXT_REQUIRED')}`
    }
    return errors
}

const api2faKey = process.env.REACT_APP_2FA_APIKEY
const url2fa = process.env.REACT_APP_2FA_URL
const appId2fa = process.env.REACT_APP_2FA_APP_ID
const storage2FA = process.env.REACT_APP_2FA_STORAGE

const useLogin = () => {
    const [openDialogOk, setOpenDialogOk] = useState<boolean>(false)
    const [alertMessageOk, setAlertMessageOk] = useState<string>('')
    const [openDialogError] = useState<boolean>(false)
    const [alertMessageError] = useState<string>('')
    const [statusCode, setStatusCode] = useState<number>()
    const loadingStore = useLoadingStore()
    const { callServiceGetProfile } = useRelogin()

    const callService = async (data: Form) => {
        const is2FA = globalThis.localStorage.getItem(`${storage2FA}`)
        if (is2FA === 'true') {
            callServiceValidateUser(data)
        } else if (is2FA === 'false') {
            callServiceLogin(data)
        } else {
            setOpenDialogOk(true)
            setAlertMessageOk('ไม่สามารถเข้าสู่ระบบได้')
        }
    }

    const handleSubmitForm = (values: any) => {
        globalThis.sessionStorage.clear()
        globalThis.localStorage.removeItem(`${process.env.REACT_APP_TOKEN_PROFILE}`)
        callService(values as Form)
    }

    const handleClickOk = () => {
        setOpenDialogOk(false)
    }

    const callServiceValidateUser = async (data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/validate-user`
            const hashISYL = encryptAES(data.isyl as string)
            const stateId = uuidv4()
            const reqParams = {
                username: data.username,
                password: hashISYL,
                state: stateId,
            }
            const result: AxiosResponse<ValidateUserResponse> = await axios.post(url, reqParams, ConfigAPI)
            if (result.status === 200) {
                setStatusCode(200)
                if (result.data.valid) {
                    const cookieToken = {
                        email: result.data.email,
                        verifyToken: result.data.verifyToken,
                        state: stateId,
                        isRememberLogin: data.rememberPassword ?? false,
                    }
                    const hashCookieToken = encryptAES(JSON.stringify(cookieToken))
                    if (data.rememberPassword) {
                        document.cookie = `${process.env.REACT_APP_TOKEN_VALIDATE}=${hashCookieToken}; path=/; max-age=2592000`
                    } else {
                        document.cookie = `${process.env.REACT_APP_TOKEN_VALIDATE}=${hashCookieToken}; path=/; max-age=3600`
                    }
                    //history.push(`/login/verify2fa?result=verify&email=${result.data.email}&state=${stateId}`)
                    globalThis.location.replace(
                        `${url2fa}/nbtc-otp-wa/verify?id=${appId2fa}&apikey=${api2faKey}&email=${result.data.email}&state=${stateId}`,
                    )
                } else if (!result.data.valid && result.data.errCode === 'EMAIL_NOT_FOUND') {
                    setOpenDialogOk(true)
                    setAlertMessageOk('ต้องการ Email กรุณาติดต่อผู้ดูแลระบบ')
                } else if (!result.data.valid && result.data.errCode === 'USER_NOT_FOUND') {
                    setOpenDialogOk(true)
                    setAlertMessageOk('Username หรือ Password ไม่ถูกต้อง')
                } else {
                    setOpenDialogOk(true)
                    setAlertMessageOk('ไม่สามารถเข้าสู่ระบบได้')
                }
            }
        } catch (error) {
            console.log('error ::', error)
        } finally {
            loadingStore.setLoading(false)
        }
    }

    const callServiceLogin = async (data: Form) => {
        try {
            loadingStore.setLoading(true)
            const url = `${process.env.REACT_APP_API}/api/auth/login`
            const hashISYL = encryptAES(data.isyl as string)
            const stateId = uuidv4()
            const reqParams = {
                username: data.username,
                password: hashISYL,
                state: stateId,
            }
            const result: AxiosResponse<AutLoginResponse> = await axios.post(url, reqParams, ConfigAPI)

            if (result.status === 200) {
                setStatusCode(200)
                if (result.data.success) {
                    globalThis.sessionStorage.setItem(
                        `${process.env.REACT_APP_ACCESS_TOKEN_NBTC}`,
                        result.data.accessToken,
                    )

                    const hashCookieToken = encryptAES(
                        JSON.stringify({ ...result.data, isRememberLogin: data.rememberPassword }),
                    )
                    if (data.rememberPassword) {
                        globalThis.localStorage.setItem(`${process.env.REACT_APP_TOKEN_PROFILE}`, hashCookieToken)
                    }

                    globalThis.sessionStorage.setItem(`${process.env.REACT_APP_TOKEN_PROFILE}`, hashCookieToken)

                    callServiceGetProfile('/', false)
                } else {
                    setOpenDialogOk(true)
                    setAlertMessageOk('ไม่สามารถเข้าสู่ระบบได้')
                }
            }
        } catch (error) {
            console.log('error ::', error)
            setOpenDialogOk(true)
            setAlertMessageOk('ไม่สามารถเข้าสู่ระบบได้')
        } finally {
            loadingStore.setLoading(false)
        }
    }

    return {
        onSubmitForm: handleSubmitForm,
        onClickOk: handleClickOk,
        setOpenDialogOk,
        setAlertMessageOk,
        openDialogOk,
        alertMessageOk,
        openDialogError,
        alertMessageError,
    }
}

export default useLogin
